import type { ArcadePalette } from "@/utils/arcade/theme";
import { drawGlow, glowText, withAlpha, type ArtKit, type Ctx2D } from "./render";
import type { Box, Metrics, Pickup, Thing } from "./vineWorld";

// Vine Runner's sprites. Everything is drawn from plain shapes with a dark
// body and a neon outline so silhouettes read at a glance on a phone. The
// colour language is fixed whatever the song's palette: a white top means
// stomp it, red spikes and teeth mean do not touch.

const TAU = Math.PI * 2;
// The one colour that means "land here": stompable tops, the boss core.
const STOMP_TOP = "hsl(225 100% 98%)";

export interface DrawKit {
  ctx: Ctx2D;
  art: ArtKit;
  palette: ArcadePalette;
  m: Metrics;
  time: number;
  pulse: number;
}

const yOf = (k: DrawKit, alt: number) => k.m.ground - alt;

function outline(ctx: Ctx2D, fill: string, stroke: string, width: number) {
  ctx.fillStyle = fill;
  ctx.fill();
  ctx.strokeStyle = stroke;
  ctx.lineWidth = width;
  ctx.stroke();
}

function eyes(ctx: Ctx2D, x: number, y: number, size: number, spread: number, look = -1) {
  ctx.fillStyle = "#fff";
  for (const side of [-1, 1]) {
    ctx.beginPath();
    ctx.ellipse(x + side * spread, y, size, size * 1.15, 0, 0, TAU);
    ctx.fill();
  }
  ctx.fillStyle = "#07070a";
  for (const side of [-1, 1]) {
    ctx.beginPath();
    ctx.arc(x + side * spread + look * size * 0.35, y + size * 0.1, size * 0.5, 0, TAU);
    ctx.fill();
  }
}

// Angry brows over a boss's eyes.
function brows(ctx: Ctx2D, x: number, y: number, size: number, spread: number, color: string) {
  ctx.strokeStyle = color;
  ctx.lineWidth = Math.max(2, size * 0.45);
  ctx.lineCap = "round";
  ctx.beginPath();
  for (const side of [-1, 1]) {
    ctx.moveTo(x + side * (spread + size * 1.1), y - size * 1.9);
    ctx.lineTo(x + side * (spread - size * 0.9), y - size * 1.2);
  }
  ctx.stroke();
}

// An equalizer column. Standable, so its top edge is a bright line.
export function drawBar(k: DrawKit, thing: Thing, box: Box, lit: number) {
  const { ctx, palette, m } = k;
  const top = yOf(k, box.a1);
  const bottom = yOf(k, box.a0);
  const height = bottom - top;
  const segment = Math.max(8, m.r * 0.55);
  const segments = Math.max(2, Math.round(height / segment));
  const step = height / segments;
  const x = box.x0;
  const w = box.x1 - box.x0;
  ctx.save();
  if (thing.dying > 0) ctx.globalAlpha = Math.max(0, thing.dying / 0.3);
  for (let s = 0; s < segments; s++) {
    const t = s / Math.max(1, segments - 1);
    ctx.fillStyle = t > 0.5 ? palette.secondary : palette.primary;
    ctx.globalAlpha *= 1;
    ctx.fillRect(x, bottom - (s + 1) * step + 1.5, w, step - 3);
  }
  if (thing.kind === "stack") {
    // Two stacked blocks and a double chevron: this one needs a double jump.
    ctx.fillStyle = withAlpha(palette.backgroundDeep, 0.9);
    ctx.fillRect(x - 1, top + height * 0.48, w + 2, 3);
    ctx.strokeStyle = palette.text;
    ctx.lineWidth = 2;
    const cx = x + w / 2;
    for (let i = 0; i < 2; i++) {
      const cy = top - m.r * 0.6 - i * m.r * 0.45;
      ctx.beginPath();
      ctx.moveTo(cx - m.r * 0.4, cy + m.r * 0.2);
      ctx.lineTo(cx, cy - m.r * 0.15);
      ctx.lineTo(cx + m.r * 0.4, cy + m.r * 0.2);
      ctx.stroke();
    }
  }
  ctx.fillStyle = "#fff";
  ctx.fillRect(x - 1, top - 1, w + 2, 3);
  ctx.restore();
  if (lit > 0.05) drawGlow(ctx, k.art, palette.primary, x + w / 2, top, w * 1.1, lit * 0.3);
}

export function drawHanging(k: DrawKit, thing: Thing, box: Box) {
  const { ctx, palette, m } = k;
  const bottom = yOf(k, box.a0);
  const x = box.x0;
  const w = box.x1 - box.x0;
  const blockTop = bottom - m.r * 2.4;
  ctx.save();
  if (thing.dying > 0) ctx.globalAlpha = Math.max(0, thing.dying / 0.3);
  ctx.strokeStyle = withAlpha(palette.secondary, 0.6);
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(x + w * 0.25, 0);
  ctx.lineTo(x + w * 0.25, blockTop);
  ctx.moveTo(x + w * 0.75, 0);
  ctx.lineTo(x + w * 0.75, blockTop);
  ctx.stroke();
  const segments = 3;
  for (let s = 0; s < segments; s++) {
    ctx.fillStyle = s === segments - 1 ? palette.danger : palette.secondary;
    const segH = (bottom - blockTop - m.r * 0.4) / segments;
    ctx.fillRect(x, blockTop + s * segH + 1.5, w, segH - 3);
  }
  // Teeth along the underside.
  ctx.fillStyle = palette.danger;
  ctx.beginPath();
  const teeth = Math.max(2, Math.round(w / (m.r * 0.6)));
  const tooth = w / teeth;
  const base = bottom - m.r * 0.4;
  for (let i = 0; i < teeth; i++) {
    ctx.moveTo(x + i * tooth, base);
    ctx.lineTo(x + (i + 0.5) * tooth, bottom);
    ctx.lineTo(x + (i + 1) * tooth, base);
  }
  ctx.fill();
  ctx.restore();
}

export function drawPillar(k: DrawKit, box: Box) {
  const { ctx, palette, m } = k;
  const top = yOf(k, box.a1);
  const bottom = m.ground;
  const x = box.x0;
  const w = box.x1 - box.x0;
  ctx.save();
  const body = ctx.createLinearGradient(x, 0, x + w, 0);
  body.addColorStop(0, withAlpha(palette.secondary, 0.55));
  body.addColorStop(0.5, palette.secondary);
  body.addColorStop(1, withAlpha(palette.secondary, 0.55));
  ctx.fillStyle = body;
  ctx.fillRect(x + w * 0.12, top + m.r * 0.5, w * 0.76, bottom - top - m.r * 0.5);
  ctx.strokeStyle = withAlpha(palette.backgroundDeep, 0.7);
  ctx.lineWidth = 2;
  for (let y = bottom - m.r * 0.6; y > top + m.r; y -= m.r * 0.7) {
    ctx.beginPath();
    ctx.moveTo(x + w * 0.12, y);
    ctx.lineTo(x + w * 0.88, y);
    ctx.stroke();
  }
  // Spiked hot cap.
  ctx.fillStyle = palette.danger;
  ctx.fillRect(x, top + m.r * 0.35, w, m.r * 0.35);
  ctx.beginPath();
  const spikes = 3;
  for (let i = 0; i < spikes; i++) {
    ctx.moveTo(x + (i / spikes) * w, top + m.r * 0.38);
    ctx.lineTo(x + ((i + 0.5) / spikes) * w, top);
    ctx.lineTo(x + ((i + 1) / spikes) * w, top + m.r * 0.38);
  }
  ctx.fill();
  ctx.restore();
}

export function drawPit(k: DrawKit, x: number, w: number) {
  const { ctx, palette, m } = k;
  const g = m.ground;
  ctx.save();
  ctx.fillStyle = "#020204";
  ctx.fillRect(x, g - 2, w, k.m.height - g + 4);
  const glow = ctx.createLinearGradient(0, g, 0, k.m.height);
  glow.addColorStop(0, withAlpha(palette.danger, 0));
  glow.addColorStop(1, withAlpha(palette.danger, 0.28));
  ctx.fillStyle = glow;
  ctx.fillRect(x, g, w, k.m.height - g);
  ctx.fillStyle = palette.danger;
  ctx.fillRect(x - 2, g - 2, 3, m.r * 0.9);
  ctx.fillRect(x + w - 1, g - 2, 3, m.r * 0.9);
  ctx.restore();
}

function crawlerBody(k: DrawKit, box: Box, thing: Thing, shell: string | null) {
  const { ctx, palette, m, time } = k;
  const w = box.x1 - box.x0;
  const cx = box.x0 + w / 2;
  const bottom = yOf(k, box.a0) - m.r * 0.25;
  const top = yOf(k, box.a1);
  const squash = thing.squash;
  const height = (bottom - top) * (1 - squash * 0.75);
  const bodyTop = bottom - height;
  // Legs.
  ctx.strokeStyle = palette.danger;
  ctx.lineWidth = Math.max(1.5, m.r * 0.12);
  ctx.lineCap = "round";
  const stride = Math.sin(time * 16 + thing.phase);
  ctx.beginPath();
  for (let i = 0; i < 3; i++) {
    const lx = box.x0 + w * (0.22 + i * 0.28);
    const swing = (i % 2 === 0 ? stride : -stride) * m.r * 0.22;
    ctx.moveTo(lx, bottom - m.r * 0.1);
    ctx.lineTo(lx + swing - m.r * 0.15, bottom + m.r * 0.25);
  }
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(box.x0, bottom);
  ctx.quadraticCurveTo(box.x0, bodyTop, cx, bodyTop);
  ctx.quadraticCurveTo(box.x1, bodyTop, box.x1, bottom);
  ctx.closePath();
  outline(ctx, palette.backgroundDeep, palette.danger, Math.max(2, m.r * 0.13));
  if (shell) {
    ctx.strokeStyle = shell;
    ctx.lineWidth = Math.max(2.5, m.r * 0.2);
    ctx.beginPath();
    ctx.moveTo(box.x0 + w * 0.2, bodyTop + height * 0.3);
    ctx.quadraticCurveTo(cx, bodyTop - height * 0.05, box.x1 - w * 0.2, bodyTop + height * 0.3);
    ctx.stroke();
  }
  if (squash < 0.5) eyes(ctx, cx - w * 0.12, bodyTop + height * 0.55, m.r * 0.2, m.r * 0.28);
}

export function drawCrawler(k: DrawKit, thing: Thing, box: Box) {
  const { ctx, palette, m } = k;
  ctx.save();
  if (thing.dying > 0) ctx.globalAlpha = Math.min(1, thing.dying / 0.25);
  if (thing.kind === "spiker") {
    crawlerBody(k, box, thing, null);
    const w = box.x1 - box.x0;
    const top = yOf(k, box.a1);
    const base = top + m.r * 0.55;
    ctx.fillStyle = palette.danger;
    ctx.beginPath();
    const spikes = 4;
    for (let i = 0; i < spikes; i++) {
      const sx = box.x0 + w * (0.12 + (i / spikes) * 0.76);
      const sw = (w * 0.76) / spikes;
      ctx.moveTo(sx, base);
      ctx.lineTo(sx + sw / 2, top - m.r * 0.05);
      ctx.lineTo(sx + sw, base);
    }
    ctx.fill();
  } else {
    crawlerBody(k, box, thing, STOMP_TOP);
  }
  if (thing.kind === "hopper" && box.a0 > m.r * 0.2) {
    ctx.strokeStyle = withAlpha(palette.text, 0.6);
    ctx.lineWidth = 2;
    const cx = (box.x0 + box.x1) / 2;
    const y = yOf(k, box.a0);
    ctx.beginPath();
    for (let i = 0; i < 4; i++) {
      ctx.moveTo(cx - m.r * 0.3, y + i * m.r * 0.18);
      ctx.lineTo(cx + m.r * 0.3, y + i * m.r * 0.18 + m.r * 0.09);
    }
    ctx.stroke();
  }
  ctx.restore();
}

export function drawFlyer(k: DrawKit, thing: Thing, box: Box) {
  const { ctx, palette, m, time } = k;
  const x = box.cx;
  const y = yOf(k, box.ca);
  const size = box.rad;
  const glider = thing.kind === "glider";
  const flap = Math.sin(time * (glider ? 7 : 15) + thing.phase);
  ctx.save();
  if (thing.dying > 0) {
    ctx.globalAlpha = Math.min(1, thing.dying / 0.25);
  }
  const span = size * (glider ? 2.9 : 2.3);
  const wing = glider ? palette.secondary : palette.danger;
  ctx.fillStyle = withAlpha(wing, 0.35);
  ctx.strokeStyle = wing;
  ctx.lineWidth = Math.max(1.5, m.r * 0.1);
  ctx.lineJoin = "round";
  for (const side of [-1, 1]) {
    const tipY = y - size * (0.45 + flap * 0.75);
    ctx.beginPath();
    ctx.moveTo(x + side * size * 0.6, y - size * 0.35);
    ctx.quadraticCurveTo(x + side * span * 0.6, tipY - size * 0.35, x + side * span, tipY);
    ctx.quadraticCurveTo(x + side * span * 0.85, y + size * 0.1, x + side * span * 0.6, y + size * 0.05);
    ctx.quadraticCurveTo(x + side * span * 0.45, y + size * 0.45, x + side * size * 0.6, y + size * 0.3);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
  }
  ctx.beginPath();
  ctx.arc(x, y, size, 0, TAU);
  outline(ctx, palette.backgroundDeep, glider ? palette.secondary : palette.danger, Math.max(2, m.r * 0.12));
  // The stompable crown.
  ctx.strokeStyle = STOMP_TOP;
  ctx.lineWidth = Math.max(2.5, m.r * 0.18);
  ctx.beginPath();
  ctx.arc(x, y, size * 0.78, Math.PI * 1.18, Math.PI * 1.82);
  ctx.stroke();
  ctx.fillStyle = "#fff";
  ctx.beginPath();
  ctx.ellipse(x - size * 0.2, y + size * 0.08, size * 0.34, size * 0.4, 0, 0, TAU);
  ctx.fill();
  ctx.fillStyle = "#07070a";
  ctx.beginPath();
  ctx.arc(x - size * 0.32, y + size * 0.12, size * 0.18, 0, TAU);
  ctx.fill();
  ctx.restore();
}

export function drawShooter(k: DrawKit, thing: Thing, box: Box, charge: number) {
  const { ctx, palette, m, art } = k;
  const w = box.x1 - box.x0;
  const bottom = yOf(k, box.a0);
  const top = yOf(k, box.a1);
  const cx = box.x0 + w / 2;
  ctx.save();
  if (thing.dying > 0) ctx.globalAlpha = Math.min(1, thing.dying / 0.25);
  const squash = 1 - thing.squash * 0.7;
  const dome = bottom - (bottom - top) * squash;
  // Barrel.
  ctx.fillStyle = palette.danger;
  ctx.fillRect(box.x0 - m.r * 0.55, dome + (bottom - dome) * 0.35, m.r * 0.9, m.r * 0.42);
  ctx.beginPath();
  ctx.moveTo(box.x0, bottom);
  ctx.lineTo(box.x0, dome + (bottom - dome) * 0.45);
  ctx.quadraticCurveTo(box.x0, dome, cx, dome);
  ctx.quadraticCurveTo(box.x1, dome, box.x1, dome + (bottom - dome) * 0.45);
  ctx.lineTo(box.x1, bottom);
  ctx.closePath();
  outline(ctx, palette.backgroundDeep, palette.danger, Math.max(2, m.r * 0.13));
  ctx.strokeStyle = STOMP_TOP;
  ctx.lineWidth = Math.max(2.5, m.r * 0.2);
  ctx.beginPath();
  ctx.moveTo(box.x0 + w * 0.22, dome + (bottom - dome) * 0.28);
  ctx.quadraticCurveTo(cx, dome - m.r * 0.05, box.x1 - w * 0.22, dome + (bottom - dome) * 0.28);
  ctx.stroke();
  // A single eye that lights up before it fires.
  ctx.fillStyle = charge > 0 ? palette.danger : "#fff";
  ctx.beginPath();
  ctx.arc(cx - w * 0.08, dome + (bottom - dome) * 0.6, m.r * 0.22, 0, TAU);
  ctx.fill();
  ctx.restore();
  if (charge > 0) drawGlow(ctx, art, palette.danger, box.x0 - m.r * 0.55, dome + (bottom - dome) * 0.45, m.r * 1.2, charge * 0.7);
}

export function drawShot(k: DrawKit, box: Box) {
  const { ctx, palette, art } = k;
  const x = box.cx;
  const y = yOf(k, box.ca);
  ctx.save();
  ctx.strokeStyle = withAlpha(palette.danger, 0.45);
  ctx.lineWidth = box.rad * 1.2;
  ctx.lineCap = "round";
  ctx.beginPath();
  ctx.moveTo(x, y);
  ctx.lineTo(x + box.rad * 3.5, y);
  ctx.stroke();
  ctx.fillStyle = palette.danger;
  ctx.beginPath();
  ctx.arc(x, y, box.rad, 0, TAU);
  ctx.fill();
  ctx.fillStyle = "#fff";
  ctx.beginPath();
  ctx.arc(x, y, box.rad * 0.5, 0, TAU);
  ctx.fill();
  ctx.restore();
  drawGlow(ctx, art, palette.danger, x, y, box.rad * 3, 0.5);
}

export function drawWave(k: DrawKit, box: Box) {
  const { ctx, palette, m } = k;
  const x0 = box.x0;
  const w = box.x1 - box.x0;
  const bottom = m.ground;
  const top = yOf(k, box.a1);
  ctx.save();
  ctx.beginPath();
  ctx.moveTo(x0 - w * 0.2, bottom);
  ctx.quadraticCurveTo(x0 + w * 0.1, top - m.r * 0.2, x0 + w * 0.55, top);
  ctx.quadraticCurveTo(x0 + w * 1.1, top + (bottom - top) * 0.2, x0 + w * 2.2, bottom);
  ctx.closePath();
  const fill = ctx.createLinearGradient(x0, top, x0, bottom);
  fill.addColorStop(0, "#fff");
  fill.addColorStop(0.35, palette.danger);
  fill.addColorStop(1, withAlpha(palette.danger, 0.4));
  ctx.fillStyle = fill;
  ctx.fill();
  ctx.restore();
}

export function drawBomb(k: DrawKit, box: Box, landX: number) {
  const { ctx, palette, m, time, art } = k;
  const x = box.cx;
  const y = yOf(k, box.ca);
  // Where it will land, and a dashed drop line down to it.
  ctx.save();
  const closeness = Math.max(0, 1 - (box.ca - box.rad) / (m.ground * 0.9));
  ctx.setLineDash([m.r * 0.3, m.r * 0.4]);
  ctx.strokeStyle = withAlpha(palette.danger, 0.25 + closeness * 0.35);
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(x, y + box.rad);
  ctx.lineTo(landX, m.ground);
  ctx.stroke();
  ctx.setLineDash([]);
  ctx.strokeStyle = withAlpha(palette.danger, 0.45 + closeness * 0.5);
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.ellipse(landX, m.ground + 2, m.r * (0.7 + closeness * 0.5), m.r * 0.25, 0, 0, TAU);
  ctx.stroke();
  ctx.fillStyle = withAlpha(palette.danger, 0.2 + closeness * 0.3);
  ctx.fill();
  ctx.beginPath();
  ctx.arc(x, y, box.rad, 0, TAU);
  outline(ctx, palette.danger, "#fff", 2);
  ctx.fillStyle = "rgba(0,0,0,0.35)";
  ctx.beginPath();
  ctx.arc(x + box.rad * 0.2, y + box.rad * 0.2, box.rad * 0.7, 0, TAU);
  ctx.fill();
  ctx.strokeStyle = palette.text;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(x + box.rad * 0.4, y - box.rad * 0.8);
  ctx.lineTo(x + box.rad * 0.7, y - box.rad * 1.3);
  ctx.stroke();
  ctx.restore();
  drawGlow(ctx, art, Math.sin(time * 30) > 0 ? palette.text : palette.danger, x + box.rad * 0.75, y - box.rad * 1.35, box.rad * 0.9, 0.9);
}

export function drawFire(k: DrawKit, thing: Thing, box: Box) {
  const { ctx, palette, time } = k;
  const w = box.x1 - box.x0;
  const bottom = k.m.ground;
  const fade = Math.max(0, Math.min(1, (thing.off - thing.age) * 3));
  ctx.save();
  ctx.globalAlpha = fade;
  const flames = 4;
  for (let i = 0; i < flames; i++) {
    const fx = box.x0 + (i + 0.5) * (w / flames);
    const flick = 0.75 + 0.25 * Math.sin(time * 22 + i * 1.7);
    const fh = (box.a1 - box.a0) * flick * (i === 1 || i === 2 ? 1 : 0.7);
    ctx.beginPath();
    ctx.moveTo(fx - w / flames / 1.6, bottom);
    ctx.quadraticCurveTo(fx - w / flames / 3, bottom - fh * 0.6, fx, bottom - fh);
    ctx.quadraticCurveTo(fx + w / flames / 3, bottom - fh * 0.6, fx + w / flames / 1.6, bottom);
    ctx.closePath();
    ctx.fillStyle = i % 2 === 0 ? palette.danger : "#ffd9a0";
    ctx.fill();
  }
  ctx.restore();
}

export function drawBeam(k: DrawKit, thing: Thing, box: Box) {
  const { ctx, palette, m, time } = k;
  const top = yOf(k, box.a1);
  const bottom = yOf(k, box.a0);
  const mid = (top + bottom) / 2;
  ctx.save();
  if (thing.age < thing.on) {
    // Telegraph: a dashed line that fills in as the shot comes.
    const warn = 1 - (thing.on - thing.age) / Math.max(0.01, thing.on);
    ctx.setLineDash([m.r * 0.6, m.r * 0.5]);
    ctx.lineDashOffset = -time * 80;
    ctx.strokeStyle = withAlpha(palette.danger, 0.5 + warn * 0.5);
    ctx.lineWidth = 2.5 + warn * 2.5;
    ctx.beginPath();
    ctx.moveTo(box.x0, mid);
    ctx.lineTo(box.x1, mid);
    ctx.stroke();
    ctx.setLineDash([]);
    const blink = Math.sin(time * 18) > 0 ? 1 : 0.5;
    glowText(ctx, thing.alt > m.r ? "STAY DOWN" : "JUMP", m.px + m.r * 4.5, mid - m.r * 1.2, Math.max(14, m.r * 0.85), palette.danger, blink * (0.7 + warn * 0.3));
  } else if (thing.age <= thing.off) {
    const fade = Math.min(1, (thing.off - thing.age) * 8);
    ctx.globalAlpha = fade;
    ctx.fillStyle = withAlpha(palette.danger, 0.55);
    ctx.fillRect(box.x0, top, box.x1 - box.x0, bottom - top);
    ctx.fillStyle = "#fff";
    ctx.fillRect(box.x0, mid - (bottom - top) * 0.18, box.x1 - box.x0, (bottom - top) * 0.36);
  }
  ctx.restore();
}

export const WARN_HURT = 0;
export const WARN_STOMP = 1;
export const WARN_STAND = 2;

// A mark on the upright warning strip for something still off the right edge:
// a bar at its height and a chevron pointing in, in the colour that says what
// to do with it (red: avoid, white: stomp, the vine colour: land on it).
export function drawWarning(k: DrawKit, a0: number, a1: number, tone: number, closeness: number) {
  const { ctx, palette, m } = k;
  const color = tone === WARN_STOMP ? STOMP_TOP : tone === WARN_STAND ? palette.primary : palette.danger;
  const mid = (a0 + a1) / 2;
  const half = Math.max(m.r * 0.35, (a1 - a0) / 2);
  const top = yOf(k, mid + half);
  const bottom = yOf(k, mid - half);
  const edge = m.width - Math.max(3, m.r * 0.18);
  const alpha = 0.3 + 0.7 * closeness;
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.fillStyle = withAlpha(palette.backgroundDeep, 0.6);
  ctx.fillRect(edge - m.r * 1.05, top - 2, m.r * 1.05 + (m.width - edge) + 2, bottom - top + 4);
  ctx.fillStyle = color;
  ctx.fillRect(edge, top, m.width - edge, bottom - top);
  ctx.strokeStyle = color;
  ctx.lineWidth = Math.max(2, m.r * 0.14);
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  const cy = (top + bottom) / 2;
  const s = m.r * 0.3;
  const cx = edge - m.r * 0.5 - closeness * m.r * 0.15;
  ctx.beginPath();
  ctx.moveTo(cx + s * 0.6, cy - s);
  ctx.lineTo(cx - s * 0.4, cy);
  ctx.lineTo(cx + s * 0.6, cy + s);
  ctx.stroke();
  ctx.restore();
}

export function drawPickup(k: DrawKit, pickup: Pickup, drawNote: (ctx: Ctx2D, x: number, y: number, size: number, color: string) => void) {
  const { ctx, palette, m, time, art } = k;
  const y = yOf(k, pickup.alt) + Math.sin(time * 4 + pickup.phase) * m.r * 0.18;
  const x = pickup.x;
  if (pickup.kind === "note") {
    drawGlow(ctx, art, palette.accent, x, y, m.r * 1.2, 0.5);
    drawNote(ctx, x, y, m.r * 0.85, "#fff");
    return;
  }
  const color = pickup.kind === "heart" ? palette.danger : palette.accent;
  drawGlow(ctx, art, color, x, y, m.r * 1.6, 0.6);
  ctx.save();
  ctx.fillStyle = withAlpha(palette.backgroundDeep, 0.8);
  ctx.strokeStyle = "#fff";
  ctx.lineWidth = 2;
  const s = m.r * 0.6;
  ctx.beginPath();
  if (pickup.kind === "heart") {
    ctx.moveTo(x, y + s * 0.9);
    ctx.bezierCurveTo(x - s * 1.4, y - s * 0.1, x - s * 0.7, y - s * 1.1, x, y - s * 0.35);
    ctx.bezierCurveTo(x + s * 0.7, y - s * 1.1, x + s * 1.4, y - s * 0.1, x, y + s * 0.9);
    ctx.fillStyle = color;
    ctx.fill();
    ctx.stroke();
  } else {
    ctx.moveTo(x, y - s);
    ctx.lineTo(x + s * 0.85, y - s * 0.55);
    ctx.lineTo(x + s * 0.7, y + s * 0.5);
    ctx.lineTo(x, y + s);
    ctx.lineTo(x - s * 0.7, y + s * 0.5);
    ctx.lineTo(x - s * 0.85, y - s * 0.55);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
  }
  ctx.restore();
}

export interface BossLook {
  type: number;
  x: number;
  alt: number;
  w: number;
  h: number;
  flash: number;
  exposed: boolean;
  slam: number;
  dying: number;
}

// The bosses share one layout: a body, angry eyes and a glowing core on top.
// The core is the weak point, and it lights up whenever the boss is low
// enough to stomp.
export function drawBoss(k: DrawKit, boss: BossLook, bass: number) {
  const { ctx, palette, m, time, art } = k;
  const x = boss.x;
  const bottom = yOf(k, boss.alt) + boss.slam * m.r * 0.4;
  const top = bottom - boss.h;
  const w = boss.w;
  const left = x - w / 2;
  const hit = boss.flash > 0;
  const body = hit ? "#fff" : palette.backgroundDeep;
  const edge = hit ? "#fff" : palette.danger;
  const lineWidth = Math.max(2.5, m.r * 0.16);
  ctx.save();
  if (boss.dying > 0) {
    ctx.translate(Math.sin(time * 70) * m.r * 0.2, 0);
    ctx.globalAlpha = Math.min(1, boss.dying);
  }
  // Shadow on the vine.
  ctx.fillStyle = "rgba(0,0,0,0.45)";
  ctx.beginPath();
  ctx.ellipse(x, m.ground + 3, w * 0.5 * Math.max(0.35, 1 - boss.alt / (m.hj * 2)), m.r * 0.25, 0, 0, TAU);
  ctx.fill();

  if (boss.type === 1) {
    // Strobe: a saucer with a ring of lights.
    const cy = top + boss.h * 0.62;
    ctx.beginPath();
    ctx.ellipse(x, cy, w * 0.54, boss.h * 0.3, 0, 0, TAU);
    outline(ctx, body, edge, lineWidth);
    for (let i = 0; i < 7; i++) {
      const lx = x - w * 0.42 + (i / 6) * w * 0.84;
      const on = (Math.floor(time * 8) + i) % 3 === 0;
      ctx.fillStyle = on ? palette.accent : withAlpha(palette.secondary, 0.7);
      ctx.beginPath();
      ctx.arc(lx, cy + boss.h * 0.1, m.r * 0.18, 0, TAU);
      ctx.fill();
    }
    ctx.beginPath();
    ctx.ellipse(x, cy - boss.h * 0.12, w * 0.36, boss.h * 0.28, 0, Math.PI, TAU);
    outline(ctx, hit ? "#fff" : withAlpha(palette.secondary, 0.35), edge, lineWidth);
    if (!hit) {
      eyes(ctx, x - w * 0.05, cy - boss.h * 0.2, m.r * 0.26, m.r * 0.42);
      brows(ctx, x - w * 0.05, cy - boss.h * 0.2, m.r * 0.26, m.r * 0.42, palette.danger);
    }
  } else if (boss.type === 3) {
    // The Drop: a huge record with a red rim.
    const cy = top + boss.h * 0.55;
    const radius = Math.min(w, boss.h) * 0.52;
    ctx.beginPath();
    ctx.arc(x, cy, radius, 0, TAU);
    outline(ctx, hit ? "#fff" : "#0b0b10", edge, lineWidth * 1.3);
    ctx.strokeStyle = "rgba(255,255,255,0.08)";
    ctx.lineWidth = 1;
    for (let g = 0.5; g < 0.95; g += 0.12) {
      ctx.beginPath();
      ctx.arc(x, cy, radius * g, 0, TAU);
      ctx.stroke();
    }
    ctx.fillStyle = palette.danger;
    for (let i = 0; i < 10; i++) {
      const a = (i / 10) * TAU + time * 2;
      // Keep the top clear: that is where the core is stomped.
      const fromTop = Math.abs(((a + 0.2 + Math.PI / 2) % TAU + TAU) % TAU - Math.PI);
      if (fromTop > Math.PI * 0.62) continue;
      ctx.beginPath();
      ctx.moveTo(x + Math.cos(a) * radius, cy + Math.sin(a) * radius);
      ctx.lineTo(x + Math.cos(a + 0.2) * radius * 1.22, cy + Math.sin(a + 0.2) * radius * 1.22);
      ctx.lineTo(x + Math.cos(a + 0.4) * radius, cy + Math.sin(a + 0.4) * radius);
      ctx.fill();
    }
    if (!hit) {
      eyes(ctx, x - radius * 0.1, cy, m.r * 0.3, m.r * 0.5);
      brows(ctx, x - radius * 0.1, cy, m.r * 0.3, m.r * 0.5, palette.danger);
    }
  } else {
    // Subwoofer and Feedback: a cabinet. Subwoofer has a pumping cone,
    // Feedback a grille and two knob eyes.
    ctx.beginPath();
    const radius = m.r * 0.5;
    ctx.moveTo(left + radius, top + boss.h * 0.12);
    ctx.lineTo(left + w - radius, top + boss.h * 0.12);
    ctx.quadraticCurveTo(left + w, top + boss.h * 0.12, left + w, top + boss.h * 0.12 + radius);
    ctx.lineTo(left + w, bottom - radius);
    ctx.quadraticCurveTo(left + w, bottom, left + w - radius, bottom);
    ctx.lineTo(left + radius, bottom);
    ctx.quadraticCurveTo(left, bottom, left, bottom - radius);
    ctx.lineTo(left, top + boss.h * 0.12 + radius);
    ctx.quadraticCurveTo(left, top + boss.h * 0.12, left + radius, top + boss.h * 0.12);
    outline(ctx, body, edge, lineWidth);
    if (boss.type === 0) {
      const cy = top + boss.h * 0.64;
      const cone = Math.min(w, boss.h) * 0.3 * (1 + bass * 0.12);
      ctx.strokeStyle = hit ? palette.danger : palette.secondary;
      ctx.lineWidth = Math.max(2, m.r * 0.12);
      for (let ring = 1; ring >= 0.35; ring -= 0.3) {
        ctx.beginPath();
        ctx.arc(x, cy, cone * ring, 0, TAU);
        ctx.stroke();
      }
      ctx.fillStyle = palette.danger;
      ctx.beginPath();
      ctx.arc(x, cy, cone * 0.2, 0, TAU);
      ctx.fill();
      if (!hit) {
        ctx.fillStyle = palette.danger;
        for (const side of [-1, 1]) {
          ctx.beginPath();
          ctx.moveTo(x + side * w * 0.12, top + boss.h * 0.28);
          ctx.lineTo(x + side * w * 0.36, top + boss.h * 0.22);
          ctx.lineTo(x + side * w * 0.32, top + boss.h * 0.33);
          ctx.closePath();
          ctx.fill();
        }
      }
    } else {
      ctx.strokeStyle = withAlpha(palette.secondary, 0.7);
      ctx.lineWidth = 2;
      for (let i = 1; i < 6; i++) {
        const gy = top + boss.h * (0.45 + i * 0.09);
        ctx.beginPath();
        ctx.moveTo(left + w * 0.12, gy);
        ctx.lineTo(left + w * 0.88, gy);
        ctx.stroke();
      }
      if (!hit) {
        eyes(ctx, x, top + boss.h * 0.32, m.r * 0.3, w * 0.22);
        brows(ctx, x, top + boss.h * 0.32, m.r * 0.3, w * 0.22, palette.danger);
      }
    }
  }
  ctx.restore();

  // The core: the weak point.
  const coreY = top + boss.h * 0.06;
  const coreR = Math.min(w, boss.h) * 0.16;
  const pulse = 0.5 + 0.5 * Math.sin(time * (boss.exposed ? 12 : 4));
  drawGlow(ctx, art, palette.primary, x, coreY, coreR * (boss.exposed ? 3.2 : 2), boss.exposed ? 0.7 + pulse * 0.3 : 0.3);
  ctx.save();
  ctx.beginPath();
  ctx.arc(x, coreY + coreR * 0.3, coreR, Math.PI, TAU);
  ctx.closePath();
  ctx.fillStyle = boss.exposed ? "#fff" : withAlpha(STOMP_TOP, 0.75);
  ctx.fill();
  ctx.strokeStyle = palette.primary;
  ctx.lineWidth = 2;
  ctx.stroke();
  if (boss.exposed) {
    // A bouncing arrow: land here.
    const ay = coreY - coreR * 1.6 - pulse * m.r * 0.4;
    ctx.fillStyle = STOMP_TOP;
    ctx.beginPath();
    ctx.moveTo(x - m.r * 0.5, ay - m.r * 0.5);
    ctx.lineTo(x + m.r * 0.5, ay - m.r * 0.5);
    ctx.lineTo(x, ay);
    ctx.closePath();
    ctx.fill();
  }
  ctx.restore();
}

export function drawBossBar(k: DrawKit, name: string, hp: number, maxHp: number, show: number, quiet: boolean) {
  if (show <= 0.01) return;
  const { ctx, palette, m } = k;
  const width = Math.min(m.width * 0.7, 420);
  const x = (m.width - width) / 2;
  const y = Math.max(104, m.height * 0.16);
  ctx.save();
  ctx.globalAlpha = show;
  if (!quiet) {
    ctx.font = `800 ${Math.round(Math.max(12, Math.min(17, m.width * 0.035)))}px Hanson, Poppins, sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "bottom";
    ctx.fillStyle = palette.text;
    ctx.fillText(name, m.width / 2, y - 6);
  }
  ctx.fillStyle = "rgba(0,0,0,0.55)";
  ctx.fillRect(x - 3, y - 3, width + 6, 14);
  const gap = 4;
  const pip = (width - gap * (maxHp - 1)) / maxHp;
  for (let i = 0; i < maxHp; i++) {
    ctx.fillStyle = i < hp ? palette.danger : "rgba(255,255,255,0.12)";
    ctx.fillRect(x + i * (pip + gap), y, pip, 8);
  }
  ctx.restore();
}
